import { useScreenClass, Typography, Box, Carousel, CarouselSlide, GridContainer, Row, Column, AlertBox } from '@vp/swan'
import { useLocalizations } from '@vp/ubik-localizations-provider'

interface BaseProduct {
  productKey: string
  hexColorValue?: string
}

interface ProductCarouselProps<T extends BaseProduct> {
  title: string
  products: T[]
  renderProductTiles: (product: T, position: number) => React.ReactNode
  emptyStateMessage: string | React.ReactNode
  carouselRef?: (node: HTMLElement | null) => void
  headerRef?: (node: HTMLElement | null) => void
  testId?: string
}

export const ProductCarousel = <T extends BaseProduct>({
  title,
  products,
  renderProductTiles: renderProductTile,
  emptyStateMessage,
  carouselRef,
  headerRef,
  testId = 'product-carousel',
}: ProductCarouselProps<T>) => {
  const { t } = useLocalizations()
  const screenClass = useScreenClass()

  const propsByScreenClass = {
    xs: { slidesToShow: 1 },
    sm: { slidesToShow: 2 },
    md: { slidesToShow: 3 },
    lg: { slidesToShow: 3 },
    xl: { slidesToShow: 3 },
  }

  return (
    <>
      <Typography textAlign="left" fontWeight="bold" mb="to-actions" ref={headerRef}>
        {title}
      </Typography>
      <Box className="qsp-carousel-products" data-testid={testId} ref={carouselRef} mb="between-subsections">
        {products.length > 0 ? (
          <Carousel
            gridGutters={true}
            accessibleTextForPrevious={t('related-product.previous-product')}
            accessibleTextForNext={t('related-product.next-product')}
            accessibleTextForDots={products.map((_, position) => t('related-product.related-product-number').replace('{0}', `${position + 1}`))}
            infinite={false}
            {...propsByScreenClass[screenClass]}
          >
            {products.map((product, position) => (
              <CarouselSlide key={`carousel-tile-${product.productKey}-${product.hexColorValue || position}`}>
                {renderProductTile(product, position)}
              </CarouselSlide>
            ))}
          </Carousel>
        ) : (
          <GridContainer>
            <Row>
              <Column span={screenClass === 'xs' ? 12 : 6}>
                <AlertBox toast>{emptyStateMessage}</AlertBox>
              </Column>
            </Row>
          </GridContainer>
        )}
      </Box>
    </>
  )
}
